/**
 * クリニック設定 — the clinic's own defaults, edited by the clinic.
 *
 * `PATCH /api/v1/clinic/settings` has existed and been clinic-admin-guarded
 * since the tenant module was written, and nothing in the browser called it: the
 * only way to change a clinic's site name, tone or keywords was for JMC to do it
 * from the platform screens. A field a customer cannot edit is a support ticket
 * with extra steps.
 *
 * The entity block is deliberately read-only here. 運営法人・代表者・広告管理責任者
 * are registered with the contract, and a clinic editing its own legal operator
 * is a change JMC has to know about - so it is shown (an article may state it, so
 * the clinic must be able to see what will be stated) and changed by JMC.
 */
import { ThemePicker } from '../components/ThemePicker.tsx';
import { useState, type ReactNode } from 'react';
import { useLoaderData, useOutletContext, useRevalidator } from 'react-router';
import {
  patch,
  type ListResponse,
  type PlatformDefaultItem,
  type Session,
} from '../api.ts';
import { Card, Empty, Pill, StatusPill } from '../components/ui.tsx';
import { Field, FieldGroup } from '../components/EditPanel.tsx';
import { ModalForm } from '../components/Modal.tsx';
import { KeywordInput } from '../components/KeywordInput.tsx';
import { Select } from '../components/Select.tsx';
import { PromptStyle, type ClinicPromptStyles } from '../components/PromptStyle.tsx';
import type { AllowedHost } from '../components/ClinicAllowedHosts.tsx';

export interface ClinicSettingsData {
  id: string;
  name: string;
  status: string;
  timezone: string;
  plan_id: string | null;
  subscription_status: string | null;
  corporate_name: string;
  representative: string;
  /** Registered, never parsed out of the address. See migration 039. */
  prefecture: string;
  city: string;
  address: string;
  phone: string;
  ad_manager_name: string;
  site_name: string | null;
  site_url: string | null;
  default_tone: string | null;
  default_keywords: string[];
  wordpress_category: string | null;
  /** The clinic's standing article length. null = the shipped 4,000〜5,000. */
  target_length: number | null;
  /** The clinic's theme colour, #rrggbb, or null for the product's default. */
  brand_color?: string | null;
}

/** What the generator uses when neither the clinic nor the article sets one. */
export const DEFAULT_LENGTH_LABEL = '4,000〜5,000文字';

/**
 * What is offered when JMC's list is empty or unreachable.
 *
 * Not the list itself: the tones come from 共通マスタ now, so JMC can add one
 * without a deploy and a clinic can hide one it never uses. This is the floor
 * under that - a screen whose only dropdown is empty is a screen nobody can
 * finish, and the three values below are the ones this product shipped with.
 */
const FALLBACK_TONES = [
  { value: 'ていねい（ですます）', label: 'ていねい（ですます）' },
  { value: 'やさしく、親しみやすく', label: 'やさしい・親しみやすい' },
  { value: '専門性が伝わる落ち着いた調子', label: '専門的・落ち着いた' },
];

export default function ClinicSettings() {
  const { clinic, prompts, tones, allowedHosts } = useLoaderData<{
    clinic: ClinicSettingsData;
    prompts: ClinicPromptStyles;
    tones: ListResponse<PlatformDefaultItem>;
    allowedHosts: { items: AllowedHost[] };
  }>();

  /*
   * JMC's list, in the order they set, falling back to what shipped. The stored
   * value is the payload's, not the label: renaming an option must not rewrite
   * the tone already saved on a clinic.
   */
  const toneOptions =
    tones.items.length > 0
      ? tones.items.map((row) => ({
          value: typeof row.payload.value === 'string' ? row.payload.value : row.label,
          label: row.label,
        }))
      : FALLBACK_TONES;
  const revalidator = useRevalidator();
  const session = useOutletContext<Session>();
  const canEdit = !!session.memberships.find((m) => m.clinicId === session.activeClinicId)
    ?.clinicAdmin;

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    siteName: clinic.site_name ?? '',
    siteUrl: clinic.site_url ?? '',
    defaultTone: clinic.default_tone ?? 'polite',
    wordpressCategory: clinic.wordpress_category ?? '',
    defaultKeywords: clinic.default_keywords,
    // Text, so clearing the box can mean "back to the shipped default" rather
    // than being represented as a zero nobody meant.
    targetLength: clinic.target_length === null ? '' : String(clinic.target_length),
  });

  const save = () =>
    patch('/clinic/settings', {
      siteName: form.siteName,
      // A blank URL is "not set" rather than an empty string the https pattern
      // would reject.
      ...(form.siteUrl.trim() ? { siteUrl: form.siteUrl.trim() } : {}),
      defaultTone: form.defaultTone,
      wordpressCategory: form.wordpressCategory,
      defaultKeywords: form.defaultKeywords,
      targetLength: form.targetLength.trim() === '' ? null : Number(form.targetLength),
    });

  /**
   * One row of the settings summary.
   *
   * The value first, and what it DOES underneath it. These are the values every
   * article this clinic produces is written against, and the screen listed them
   * as bare terms - 「文体・トーン: polite」 says nothing about the fact that every
   * store inherits it unless it overrides it.
   */
  const Row = ({
    label,
    value,
    note,
  }: {
    label: string;
    value: ReactNode;
    note: string;
  }) => (
    <div className="setrow">
      <span className="setrow__label">{label}</span>
      <span className="setrow__value">{value}</span>
      <span className="setrow__note muted small">{note}</span>
    </div>
  );

  const unset = <span className="muted">未登録</span>;

  return (
    <>
      <Card
        title="クリニック設定"
        caption="このクリニックの記事すべてに使われる既定値です。店舗ごとに上書きできます。"
        actions={
          canEdit ? (
            <button className="btn btn--sm" type="button" onClick={() => setEditing(true)}>
              設定を編集
            </button>
          ) : (
            <span className="muted small">閲覧のみ（変更はクリニック管理者が行います）</span>
          )
        }
      >
        {/*
          Three groups, in the order somebody reads them: who this clinic is on
          the page, how its articles are written, and what it is contracted for.
          It was one undifferentiated list of six terms, plus the contract as a
          separate card at the bottom of the screen.
        */}
        <p className="fieldgroup">
          <span className="fieldgroup__title">サイト情報</span>
          <span className="fieldgroup__note">記事の署名・構造化データに使われます</span>
        </p>
        <div className="setgrid">
          <Row
            label="サイト名"
            value={clinic.site_name || unset}
            note="記事の署名と構造化データに入ります"
          />
          <Row
            label="サイトURL"
            value={
              clinic.site_url ? (
                <a href={clinic.site_url} target="_blank" rel="noreferrer">
                  {clinic.site_url}
                </a>
              ) : (
                unset
              )
            }
            note="サイトから情報を読み取るときの起点にもなります"
          />
          <Row
            label="WordPressカテゴリ"
            value={clinic.wordpress_category || unset}
            note="書き出したときの既定のカテゴリ名です"
          />
        </div>

        <p className="fieldgroup">
          <span className="fieldgroup__title">記事の書き方</span>
          <span className="fieldgroup__note">店舗ごとに上書きできます</span>
        </p>
        <div className="setgrid">
          <Row
            label="文体・トーン"
            value={
              toneOptions.find((tone) => tone.value === clinic.default_tone)?.label ??
              clinic.default_tone ?? <span className="muted">未設定</span>
            }
            note="記事全体の語り口。ペルソナの希望トーンが優先されます"
          />
          <Row
            label="本文の目安文字数"
            value={
              clinic.target_length ? (
                `${clinic.target_length.toLocaleString('ja-JP')}文字前後`
              ) : (
                <span className="muted">{DEFAULT_LENGTH_LABEL}</span>
              )
            }
            note={clinic.target_length ? '記事ごとに変更できます' : '既定値。記事ごとに変更できます'}
          />
          <Row
            label="対策キーワード（既定）"
            value={
              clinic.default_keywords.length > 0 ? (
                <span className="chips">
                  {clinic.default_keywords.map((keyword) => (
                    <span key={keyword} className="tag">
                      {keyword}
                    </span>
                  ))}
                </span>
              ) : (
                unset
              )
            }
            note="記事ごとのキーワードと合わせて生成に渡されます"
          />
        </div>

        <p className="fieldgroup">
          <span className="fieldgroup__title">契約</span>
          <span className="fieldgroup__note">プランと契約状態はJMCが管理します</span>
        </p>
        <div className="setgrid">
          <Row label="クリニック名" value={clinic.name} note="請求と契約に使われる名称です" />
          <Row label="プラン" value={clinic.plan_id ?? unset} note="変更はJMCへご依頼ください" />
          <Row
            label="契約状態"
            value={
              clinic.subscription_status ? (
                <StatusPill status={clinic.subscription_status} />
              ) : (
                <span className="muted">—</span>
              )
            }
            note="停止中は記事の生成ができません"
          />
        </div>
      </Card>

      {/*
        Read-only, and its own Card rather than a row inside the edit dialog: a
        control somebody cannot change, sitting in a form whose every other
        control they can, reads as broken rather than as deliberate.
      */}
      <Card
        title="AIが読み取れるドメイン"
        caption="JMCが許可したドメインです。変更はJMCの担当者にご依頼ください。サイトURLのドメインは常に読み取れます。"
      >
        {allowedHosts.items.length === 0 ? (
          <Empty>
            追加のドメインはありません。上のサイトURLと同じドメインのページを読み取れます。
          </Empty>
        ) : (
          <div className="setgrid">
            {allowedHosts.items.map((entry) => (
              <Row
                key={entry.id}
                label={entry.host}
                value={
                  <Pill tone={entry.include_subdomains ? 'ok' : 'default'}>
                    {entry.include_subdomains ? 'サブドメインも含む' : 'このドメインのみ'}
                  </Pill>
                }
                note={
                  entry.include_subdomains
                    ? 'このドメインと、その下のすべてのサブドメインを読み取れます'
                    : 'このドメインちょうどのページのみ読み取れます'
                }
              />
            ))}
          </div>
        )}
      </Card>

      <ModalForm
        open={editing}
        onClose={() => setEditing(false)}
        title="クリニック設定を編集"
        caption="ここで決めた値が、全店舗の記事の出発点になります。店舗ごとに上書きできます。"
        wide
        onSubmit={save}
      >
        <FieldGroup title="サイト情報" note="記事の署名・構造化データに使われます" />
        <Field
          label="サイト名"
          htmlFor="cs-site-name"
          span="wide"
          hint="記事の署名や構造化データに使用します。"
        >
          <input
            id="cs-site-name"
            maxLength={200}
            value={form.siteName}
            onChange={(e) => setForm({ ...form, siteName: e.target.value })}
          />
        </Field>
        <Field label="サイトURL" htmlFor="cs-site-url" hint="https のみ登録できます。">
          <input
            id="cs-site-url"
            type="url"
            maxLength={2000}
            placeholder="https://"
            value={form.siteUrl}
            onChange={(e) => setForm({ ...form, siteUrl: e.target.value })}
          />
        </Field>
        <Field
          label="WordPressカテゴリ"
          htmlFor="cs-wp"
          hint="書き出したときの既定のカテゴリ名です。"
        >
          <input
            id="cs-wp"
            maxLength={120}
            value={form.wordpressCategory}
            onChange={(e) => setForm({ ...form, wordpressCategory: e.target.value })}
          />
        </Field>

        <FieldGroup title="記事の書き方" note="店舗ごとに上書きできます" />
        <Field label="文体・トーン" htmlFor="cs-tone" hint="記事全体の語り口です。">
          <Select
            id="cs-tone"
            value={form.defaultTone}
            onChange={(defaultTone) => setForm({ ...form, defaultTone })}
            options={toneOptions}
          />
        </Field>
        {/*
          要件定義 06 makes the body length a management value on the clinic, and
          FR-020 makes clinic settings a clinic-admin screen - so it is here
          rather than in the JMC prompt. A writer can still override it on one
          article.
        */}
        <Field
          label="本文の目安文字数"
          htmlFor="cs-length"
          hint="空欄で既定（4,000〜5,000文字）。記事ごとに変更できます。"
        >
          <input
            id="cs-length"
            type="number"
            min={500}
            max={20000}
            step={100}
            placeholder="4500"
            value={form.targetLength}
            onChange={(e) => setForm({ ...form, targetLength: e.target.value })}
          />
        </Field>
        <Field
          label="対策キーワード（既定）"
          htmlFor="cs-keywords"
          span="full"
          hint="記事ごとのキーワードと合わせて生成に渡されます。"
        >
          <KeywordInput
            id="cs-keywords"
            value={form.defaultKeywords}
            onChange={(defaultKeywords) => setForm({ ...form, defaultKeywords })}
          />
        </Field>
      </ModalForm>

      <Card
        title="運営法人"
        caption="記事に記載できる運営者情報です。契約に紐づく登録内容のため、変更はJMCが行います。"
        actions={<Pill tone="muted">JMCが管理</Pill>}
      >
        {clinic.corporate_name || clinic.representative || clinic.ad_manager_name ? (
          <div className="setgrid">
            <Row
              label="運営法人名"
              value={clinic.corporate_name || unset}
              note="記事末尾の運営者情報に記載されます"
            />
            <Row label="代表者" value={clinic.representative || unset} note="法人の代表者名です" />
            <Row
              label="本社所在地"
              value={clinic.address || unset}
              note="店舗の住所とは別の登録です"
            />
            <Row label="代表電話" value={clinic.phone || unset} note="記事の問い合わせ先に使えます" />
            <Row
              label="広告管理責任者"
              value={clinic.ad_manager_name || unset}
              note="医療広告ガイドラインで求められる登録です"
            />
          </div>
        ) : (
          <Empty>
            運営法人の情報が未登録です。記事に運営者を記載する場合は、JMCの担当者に登録を依頼してください。
          </Empty>
        )}
      </Card>

      {/*
        How this clinic's articles are written, beside what they are written
        from. The rules and the prohibitions stay with JMC; the voice is theirs.
      */}
      <PromptStyle
        data={prompts}
        canRequest={canEdit}
        onSent={() => void revalidator.revalidate()}
      />

      {/* The colour the clinic's screens are drawn in. Clinic admins decide it. */}
      <ThemePicker endpoint="/clinic/theme" value={clinic.brand_color ?? null} canEdit={canEdit} />

    </>
  );
}
